import * as Sentry from "@sentry/react"

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN

function enabled() {
  return Boolean(SENTRY_DSN) && SENTRY_DSN !== "your-sentry-dsn"
}

export function initSentry() {
  if (!enabled()) {
    console.log("[Sentry] No DSN configured — skipping init")
    return
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    // Enabled whenever a real DSN exists (not the placeholder). This makes
    // capture work on Vercel regardless of how MODE/PROD resolve, and the
    // `enabled()` guard above already blocks the placeholder case.
    enabled: !!SENTRY_DSN && SENTRY_DSN !== "your-sentry-dsn",

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllInputs: true, // privacy: mask passwords
      }),
    ],

    // Capture ALL transactions for now (dial down once volume is known).
    tracesSampleRate: 1.0,

    // Session replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // No beforeSend filter — the `enabled` flag is the single gate.
  })
}

// Set user context when logged in
export function setSentryUser(
  user: { id: string; email: string; role: string } | null,
) {
  if (!enabled()) return
  Sentry.setUser(user)
}

// Add context tags
export function setSentryContext(key: string, context: Record<string, unknown>) {
  if (!enabled()) return
  Sentry.setContext(key, context)
}

// Manually capture an error
export function captureError(error: Error, context?: Record<string, unknown>) {
  if (!enabled()) return
  Sentry.withScope((scope) => {
    if (context) scope.setExtras(context)
    Sentry.captureException(error)
  })
}

export { Sentry }
